import React from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import { getDeveloperPageUrl, NIRMAL_LABS_DEVELOPER_ID } from '../../utils/googlePlayUtils';
import GooglePlayBadge from '../GooglePlayBadge/GooglePlayBadge';
import './ProductsPage.css';

const ProductsPage = () => {
  const apps = [
    {
      id: 'aarti',
      title: 'Aarti Sangrah',
      description: 'Hindu Religious Aarti Collection with high-quality audio and lyrics in Hindi & English',
      category: 'Books & Reference',
      downloads: '10K+',
      rating: '4.0',
      icon: 'https://ik.imagekit.io/l9paoajth/app_logos/logo_aarti.webp?updatedAt=1759892173817&tr=w-120,h-120',
      features: [
        'Complete Aarti Collection',
        'High-Quality Audio',
        'Hindi & English Lyrics',
        'Aarti Tools & Wallpapers'
      ],
      link: '/aarti'
    },
    {
      id: 'novel',
      title: 'Novels & Books',
      description: 'English-Offline Reading with 100+ popular books and text-to-speech functionality',
      category: 'Books & Reference',
      downloads: '1M+',
      rating: '4.6',
      icon: 'https://ik.imagekit.io/l9paoajth/app_logos/logo_novel.webp?updatedAt=1759892173676&tr=w-120,h-120',
      features: [
        '100+ Popular Books',
        'Text-to-Speech',
        'Offline Reading',
        'Reading Tools'
      ],
      link: '/novel'
    },
    {
      id: 'media-player',
      title: 'Media Player',
      description: 'Audio Video Mp3 Player with universal format support and high-definition playback',
      category: 'Video Players & Editors',
      downloads: '50K+',
      rating: '4.1',
      icon: 'https://ik.imagekit.io/l9paoajth/app_logos/logo_media_player.webp?updatedAt=1759892173851&tr=w-120,h-120',
      features: [
        'Universal Format Support',
        'High-Definition Playback',
        'Equalizer & Audio Effects',
        'Subtitle & Playlist Support'
      ],
      link: '/media-player'
    }
  ];

  return (
    <>
      <Helmet>
        <title>All Apps - Nirmal Labs | Download Android Apps from Google Play</title>
        <meta name="description" content="Browse all Android apps by Nirmal Labs. Download Aarti Sangrah, Novels & Books, Media Player and more. 1M+ downloads, 4.5+ rating." />
        <meta name="keywords" content="Nirmal Labs apps, Android apps, Aarti app, Novel books, Media player, English Hindi learning, Hindu calendar, download apps" />
        <meta property="og:title" content="All Apps - Nirmal Labs" />
        <meta property="og:description" content="Browse all Android apps by Nirmal Labs. Download Aarti Sangrah, Novels & Books, Media Player and more." />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://nirmallabs.com/products" />
        <meta property="og:image" content="https://nirmallabs.com/images/nirmal-labs-products.jpg" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="All Apps - Nirmal Labs" />
        <meta name="twitter:description" content="Browse all Android apps by Nirmal Labs. Download Aarti Sangrah, Novels & Books, Media Player and more." />
        <meta name="twitter:image" content="https://nirmallabs.com/images/nirmal-labs-products.jpg" />
        <link rel="canonical" href="https://nirmallabs.com/products" />
        
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "Organization",
            "name": "Nirmal Labs",
            "url": "https://nirmallabs.com",
            "logo": "https://ik.imagekit.io/l9paoajth/nirmal_labs/logos/logo_only_transparent.png?updatedAt=1759949538718&tr=w-200,h-200",
            "description": "Nirmal Labs develops daily useful Android applications that fulfill users' everyday needs.",
            "sameAs": [
              "https://play.google.com/store/apps/dev?id=5561916917816890433"
            ],
            "hasOfferCatalog": {
              "@type": "OfferCatalog",
              "name": "Nirmal Labs Apps",
              "itemListElement": apps.map((app, index) => ({
                "@type": "SoftwareApplication",
                "position": index + 1,
                "name": app.title,
                "description": app.description,
                "applicationCategory": app.category,
                "operatingSystem": "Android",
                "offers": {
                  "@type": "Offer",
                  "price": "0",
                  "priceCurrency": "USD"
                },
                "aggregateRating": {
                  "@type": "AggregateRating",
                  "ratingValue": app.rating,
                  "ratingCount": "100+"
                }
              }))
            }
          })}
        </script>
      </Helmet>

      <div className="products-page">
        {/* Hero Section */}
        <section className="hero-section">
          <div className="container">
            {/* Breadcrumb Navigation */}
            <nav className="breadcrumb" aria-label="Breadcrumb">
              <ol className="breadcrumb-list">
                <li className="breadcrumb-item">
                  <Link to="/" className="breadcrumb-link">Home</Link>
                </li>
                <li className="breadcrumb-separator" aria-hidden="true">/</li>
                <li className="breadcrumb-item breadcrumb-current" aria-current="page">
                  All Apps
                </li>
              </ol>
            </nav>

            <div className="hero-content">
              <div className="hero-logo">
                <img 
                  src="https://ik.imagekit.io/l9paoajth/nirmal_labs/logos/logo_full_transparent_light.png?updatedAt=1759949302125&tr=w-300,h-100" 
                  alt="Nirmal Labs Logo" 
                  className="hero-logo-img"
                />
              </div>
              <h1 className="hero-title">Nirmal Labs</h1>
              <h2 className="hero-subtitle">All Our Apps</h2>
              <p className="hero-description">
                Discover our complete collection of Android apps designed to make your daily life easier and more convenient.
              </p>
              <div className="hero-buttons">
                <GooglePlayBadge 
                  href={getDeveloperPageUrl(NIRMAL_LABS_DEVELOPER_ID)}
                  size="large"
                  ariaLabel="Download Nirmal Labs apps from Google Play Store"
                />
                <Link to="/" className="btn-secondary">
                  Back to Home
                </Link>
              </div>
            </div>
          </div>
        </section>

        {/* Apps Grid Section */}
        <section className="apps-grid-section">
          <div className="container">
            <h2 className="section-title">Our App Collection</h2>
            <div className="apps-grid">
              {apps.map((app) => (
                <div key={app.id} className="app-card">
                  <div className="app-card-header">
                    <div className="app-icon">
                      <img 
                        src={app.icon} 
                        alt={`${app.title} App`}
                        onError={(e) => {
                          e.target.src = 'https://via.placeholder.com/120x120/31B8D6/FFFFFF?text=' + app.title.charAt(0);
                        }}
                      />
                    </div>
                    <div className="app-info">
                      <h3 className="app-title">{app.title}</h3>
                      <p className="app-category">{app.category}</p>
                      <div className="app-stats">
                        <span className="downloads">{app.downloads} Downloads</span>
                        <span className="rating">⭐ {app.rating}</span>
                      </div>
                    </div>
                  </div>
                  
                  <div className="app-card-body">
                    <p className="app-description">{app.description}</p>
                    
                    <div className="app-features">
                      <h4>Key Features:</h4>
                      <ul>
                        {app.features.map((feature, index) => (
                          <li key={index}>{feature}</li>
                        ))}
                      </ul>
                    </div>
                  </div>
                  
                  <div className="app-card-footer">
                    <Link to={app.link} className="btn-primary">
                      View Details
                    </Link>
                    <GooglePlayBadge 
                      href={`https://play.google.com/store/apps/details?id=com.nirmallabs.${app.id === 'aarti' ? 'aarti.hindiaarti' : app.id === 'novel' ? 'novelsbooksinenglish' : 'mediaplayer.musicplayer.videoplayer'}`}
                      size="standard"
                      ariaLabel={`Download ${app.title} from Google Play Store`}
                    />
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="products-cta-section">
          <div className="container">
            <div className="products-cta-content">
              <div className="cta-logo">
                <img 
                  src="https://ik.imagekit.io/l9paoajth/nirmal_labs/logos/logo_only_transparent.png?updatedAt=1759949538718&tr=w-60,h-60" 
                  alt="Nirmal Labs Logo" 
                  className="cta-logo-img"
                />
              </div>
              <h2>Download All Our Apps</h2>
              <p>Get the complete Nirmal Labs experience with all our apps</p>
              <div className="cta-buttons">
                <GooglePlayBadge 
                  href={getDeveloperPageUrl(NIRMAL_LABS_DEVELOPER_ID)}
                  size="large"
                  ariaLabel="View all Nirmal Labs apps on Google Play Store"
                />
              </div>
            </div>
          </div>
        </section>
      </div>
    </>
  );
};

export default ProductsPage;
